<template>
  <div class="container" v-if="!formSendError">
    <form class="form form--initialized form--desktop-horizontal form--bg-navy-main"
          :class="{ 'form--with-cta': date }"
          ref="form" @submit.prevent="handleSubmit">
      <a v-if="date" class="cta-link cta-link--scalable" href="#et-form">
        <div class="cta-link__inner">
        <span class="cta-link__text cta-link__text--large">
            {{ t('et.until') }}<br>{{ date }}
        </span>
        </div>
      </a>
      <div class="form__header">
        <p class="headline headline--accent headline--accent-md">{{ t('et.subtitle') }}</p>
        <h2 class="headline headline--h2" v-if="headline1Available || !!bookingInfo" v-html="title"></h2>
        <h1 class="headline headline--h1" v-else v-html="title"></h1>
        <p v-if="description">{{ description }}</p>
      </div>
      <div class="form__body">
        <div class="form__group form__group--with-border-bottom">
          <div role="group" class="form__control form__control--desktop-horizontal" :aria-labelledby="'step1-' + uniqueId">
            <div class="form__control-header">
              <div class="form__control-icon">
                <span class="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" :aria-labelledby="'step1-title-' + uniqueId"><title :id="'step1-title-' + uniqueId">{{ t('et.steps.1.step') }}</title>
                    <path d="M21.137 21.112v2.033h-8.278v-2.033h3.104v-7.76l-2.711.73v-1.997l4.834-1.445v10.472h3.05Z"></path>
                    <path fill-rule="evenodd" d="M34 17c0 9.389-7.611 17-17 17S0 26.389 0 17 7.611 0 17 0s17 7.611 17 17Zm-1.784 0c0 8.404-6.812 15.216-15.216 15.216S1.784 25.404 1.784 17 8.596 1.784 17 1.784 32.216 8.596 32.216 17Z" clip-rule="evenodd"></path>
                  </svg>
                </span>
              </div>
              <div class="form__control-text">
                <div class="form__control-headlines" :id="'step1-' + uniqueId">
                  <span class="form__control-headline headline headline--accent headline--accent-sm ">{{ t('et.steps.1.subtitle') }}</span>
                  <span class="form__control-headline headline headline--h4 ">{{ t('et.steps.1.title') }}</span>
                </div>
              </div>
            </div>
            <div class="form__control-content">
              <div class="form__control-content-inner form__control-content-inner--no-border">
                <div class="form__row">
                  <div class="row">
                    <LocationFinderSelect v-model="data.locationfinder"
                                          @validation-change="handleStudioValidation"
                                          :force-validation="forceValidationOnSubmit"
                                          :inverted="true"
                                          id="et-studio"
                                          :studios="apiJson.studios"
                                          :disableBiaStudios="data.bia"
                                          :rules="studioRules"></LocationFinderSelect>
                  </div>
                </div>
                <div class="form__row" v-if="data.locationfinder && selectedStudio">
                  <div class="row">
                    <p class="headline headline--h5" style="margin-bottom: 0">Kieser Studio</p>
                    <div class="form__divider form__divider--desktop-display-none"></div>
                    <p>
                      <strong>Kieser Studio {{ selectedStudio.name }}</strong><br />{{ selectedStudio.street }}<br />{{ selectedStudio.plz }} {{ selectedStudio.city }}
                    </p>
                    <span style="display: inline-block; width: auto;" v-if="data.bia && !selectedStudio.bia" class="badge badge--accent">{{ t('locationfinder.nobia') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="form__group">
          <div class="form__row">
            <fieldset class="form__control form__control--desktop-horizontal" :aria-labelledby="'step3-' + uniqueId" :aria-describedby="'step4-' + uniqueId">
              <div class="form__control-header">
                <div class="form__control-icon">
                  <span class="icon">
                     <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" :aria-labelledby="'step3-title-' + uniqueId"> <title :id="'step3-title-' + uniqueId">{{ t('et.steps.2.step') }}</title>
                       <path fill-rule="evenodd" d="M13.136 20.782c-.06.185-.112.378-.156.58a8.442 8.442 0 0 0-.175 1.783h8.367v-2.033h-5.887a2.87 2.87 0 0 1 .49-1.044 4.563 4.563 0 0 1 .884-.91c.35-.28.743-.544 1.177-.794.607-.35 1.154-.71 1.642-1.079a4.302 4.302 0 0 0 1.16-1.302c.285-.506.427-1.139.427-1.9 0-.69-.157-1.308-.473-1.856a3.259 3.259 0 0 0-1.355-1.302c-.589-.32-1.3-.482-2.132-.482-.642 0-1.24.114-1.793.34a4.348 4.348 0 0 0-1.427.945 4.034 4.034 0 0 0-.919 1.41l1.891.856c.035-.088.073-.172.115-.253a2.49 2.49 0 0 1 .732-.88c.381-.28.86-.42 1.437-.42.547 0 .98.155 1.302.464.321.304.481.714.481 1.231 0 .375-.095.708-.285 1a3.097 3.097 0 0 1-.731.775 9.07 9.07 0 0 1-.982.652c-.547.315-1.07.669-1.57 1.061a7.37 7.37 0 0 0-1.32 1.365 6.115 6.115 0 0 0-.9 1.793Z" clip-rule="evenodd"></path>
                       <path fill-rule="evenodd" d="M17 32.216c8.404 0 15.216-6.812 15.216-15.216S25.404 1.784 17 1.784 1.784 8.596 1.784 17 8.596 32.216 17 32.216ZM17 34c9.389 0 17-7.611 17-17S26.389 0 17 0 0 7.611 0 17s7.611 17 17 17Z" clip-rule="evenodd"></path>
                    </svg>
                  </span>
                </div>
                <div class="form__control-text">
                  <div class="form__control-headlines" :id="'step3-' + uniqueId">
                    <span class="form__control-headline  headline headline--h4 ">{{ t('et.steps.3.title') }}</span>
                  </div>
                  <div class="form__control-description" id="nS67Cs">{{ t('et.steps.3.copy') }}</div>
                </div>
              </div>

              <!-- Input Element -->
              <div class="form__control-content">
                <div class="form__control-content-inner form__control-content-inner--no-border">
                  <div class="form__row">
                    <div class="row">
                      <div class="col-12 col-xl-6">
                        <SelectField :label="t('et.salutation.label')"
                                     id="et-salutation"
                                     v-model="data.salutation"
                                     :options="tm('et.salutation.options')"
                                     @validation-change="handleSalutationValidation"
                                     :force-validation="forceValidationOnSubmit"
                                     :rules="salutationRules"
                                     :inverted="true" />
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 col-xl-6">
                        <InputField :label="t('et.firstname.label')"
                                    id="et-firstname"
                                    v-model="data.firstname"
                                    :inverted="true"
                                    @validation-change="handleFirstNameValidation"
                                    :force-validation="forceValidationOnSubmit"
                                    :rules="firstnameRules"
                                    :placeholder="t('et.firstname.placeholder')"
                                    autocomplete="given-name" />
                      </div>
                      <div class="col-12 col-xl-6">
                        <InputField :label="t('et.lastname.label')"
                                    id="et-lastname"
                                    v-model="data.lastname"
                                    :inverted="true"
                                    @validation-change="handleLastNameValidation"
                                    :force-validation="forceValidationOnSubmit"
                                    :rules="lastnameRules"
                                    :placeholder="t('et.lastname.placeholder')"
                                    autocomplete="family-name" />
                      </div>
                    </div>
                    <div class="row">
                      <InputField :label="t('et.email.label')"
                                  id="et-email"
                                  v-model="data.email"
                                  :inverted="true"
                                  type="email"
                                  @validation-change="handleEmailValidation"
                                  :force-validation="forceValidationOnSubmit"
                                  :rules="emailRules"
                                  :placeholder="t('et.email.placeholder')"
                                  autocomplete="email" />
                    </div>
                    <div class="row">
                      <PhoneSelectInput :label="t('et.phone.label')"
                                        id="et-phone"
                                        v-model="data.phone"
                                        :inverted="true"
                                        :cc="countryCodes"
                                        @validation-change="handlePhoneValidation"
                                        :force-validation="forceValidationOnSubmit"
                                        :rules="phoneRules"
                                        :placeholder="t('et.phone.placeholder')"
                                        autocomplete="tel" />
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
        <div class="form__footer-panel">
          <div class="form__row form__row--mb-lg">
            <div class="row">
              <div class="col-12">
                <p>{{ t('et.privacy.labelPreLink') }}<a :href="countryLanguagePath + t('et.privacy.link')" class="link link--negative" target="_blank">{{ t('et.privacy.labelLink') }}</a>{{ t('et.privacy.labelPostLink') }}</p>
              </div>
            </div>
          </div>
          <div class="form__row form__row--text-center form__row--text-center-not-in-desktop">
            <Submit :loading="isSubmitLoading"
                    type="btn--accent-main"
                    :label="button_text" />
          </div>
        </div>
      </div>
      <div class="form__special-note" v-if="campaignHint">
        <p id="campaign-hint" v-if="campaignHint" v-html="campaignHint"></p>
      </div>
    </form>
  </div>
  <div class="container" aria-live="polite" v-if="!isFormSend && formSendError">
    <div class="simple-2col  simple-2col--with-navy-bg simple-2col--gap-lg">
      <div class="simple-2col__col simple-2col__col--with-navy-bg simple-2col__col--align-center simple-2col__col--no-padding-top simple-2col__col--no-padding-x">
        <div class="simple-2col__header">
          <h2 class="headline headline--h1">{{ t('et.error.title') }}</h2>
        </div>
        <div class="simple-2col__content">
          <p>{{ t('et.error.copy') }}</p>
        </div>
      </div>
      <div class="simple-2col__col simple-2col__col--with-blue-bg simple-2col__col--align-center">
        <span class="icon icon--lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34">
            <path d="M17 30C9.832 30 4 24.168 4 17S9.832 4 17 4s13 5.832 13 13-5.832 13-13 13Zm0-24.953C10.408 5.047 5.047 10.408 5.047 17S10.408 28.953 17 28.953 28.953 23.592 28.953 17 23.592 5.047 17 5.047Z"></path>
            <path fill-rule="evenodd" d="M11.428 14.2a1.019 1.019 0 0 0-.028.24v5.959c0 .571.461.993.978.993h9.226a.986.986 0 0 0 .979-.993v-5.991l-5.295 3.146a.585.585 0 0 1-.609-.01l-5.251-3.343Zm.832-.746 4.742 3.02 4.97-2.954a.962.962 0 0 0-.368-.073h-9.226c-.04 0-.08.002-.118.007Zm-1.978.986c0-1.111.915-2.053 2.096-2.053h9.226c1.181 0 2.097.941 2.097 2.053v5.959c0 1.111-.916 2.053-2.097 2.053h-9.226c-1.18 0-2.096-.942-2.096-2.053v-5.96Z" clip-rule="evenodd"></path>
          </svg>
        </span>
        <p class="headline headline--h4">{{ t('et.error.email') }}</p>
        <p>{{ t('et.error.emailCopy') }} <a class="link link--negative" href="mailto:info@kieser.com">info@kieser.com</a>.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import {reactive, ref, computed, shallowRef, useTemplateRef, watch, useId, onBeforeMount, onMounted} from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import InputField from './components/fields/InputField.vue';
import Submit from './components/fields/Submit.vue';
import {
  checkCookies,
  getBrowser, getCookie,
  getCountryAndLanguagePathFromUrl,
  getCountryFromUrl,
  getLanguageFromUrl,
} from '../../abstracts/utilities.js';
import {getStudioUidByName, getStudioByName, normalizeEmailForUserHash, hashSha256, trackData} from './utilities.js';
import SelectField from './components/fields/SelectField.vue';
import LocationFinderSelect from './components/fields/LocationFinderSelect.vue';
import Checkbox from './components/fields/Checkbox.vue';
import DateRadioButtons from './components/et-fields/DateRadioButtons.vue';
import PhoneSelectInput from './components/et-fields/PhoneSelectInput.vue';
import Error from './components/fields/Error.vue';
import InterestCheckbox from './components/et-fields/InterestCheckbox.vue';
import {initialAdsCookie} from '../../abstracts/kies_ad_et.js';
import Form from '/vendor/skygate/kieser-patternlab/source/javascript/components/form.js';

const { t, tm } = useI18n();
const success = useTemplateRef('success');
const error = useTemplateRef('error');
const uniqueId = useId();

const props = defineProps({
  apiJson: {
    type: Object,
    default: () => {}
  }
});

const countryLanguagePath = window.location.origin + '/' + getCountryAndLanguagePathFromUrl();
const countryCodes = [
    {country: 'de', code: '49'},
    {country: 'at', code: '43'},
    {country: 'ch', code: '41'},
    {country: 'lu', code: '352'},
    {country: 'fr', code: '33'},
    {country: 'be', code: '32'},
    {country: 'nl', code: '31'}
]

const bookingInfo = ref(props.apiJson.booking);

const actionHelptext = computed(() => {
  if(bookingInfo.value && bookingInfo.value.checkbox_et_text.length) {
    return bookingInfo.value.checkbox_et_text
  } else {
    return t('et.action.helptext');
  }
});
const biaHelptext = computed(() => {
  if(bookingInfo.value && bookingInfo.value.checkbox_bia_text.length) {
    return bookingInfo.value.checkbox_bia_text
  } else {
    return t('et.bia.helptext') + ' ' + t('et.bia.price.' + getCountryFromUrl());
  }
});
const title = computed(() => {
  if(bookingInfo.value && bookingInfo.value.title?.length) {
    let v = bookingInfo.value.title;

    // every campaign always needs a hint text with a star
    if(v.indexOf('*') !== -1) {
      v = v.replace('*', '<span aria-describedby="campaign-hint">*</span>');
    }

    return v
  } else {
    return t('et.title');
  }
});

const headline1Available = computed(() => {
  return document.getElementsByTagName('h1').length;
});

const date = computed(() => {
  if(bookingInfo.value && bookingInfo.value.date) {
    let timestamp = bookingInfo.value.date * 1000;
    let date = new Date(timestamp);

    return date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear();
  } else {
    return null;
  }
});

const campaignHint = computed(() => {
  if(bookingInfo.value && bookingInfo.value.title?.length && bookingInfo.value.date) {
    if(bookingInfo.value.title.indexOf('*') !== -1) {
      return '*' + t('et.campaign.copy1') + date.value + t('et.campaign.copy2');
    } else {
      return false;
    }
  } else {
    return false;
  }
});

const description = computed(() => {
  if(bookingInfo.value && bookingInfo.value.description?.length) {
    let string = bookingInfo.value.description;
    string = string.replace('###DATE###', date.value);
    return string;
  } else {
    return null;
  }
});

const button_text = computed(() => {
  if(bookingInfo.value && bookingInfo.value.button_text?.length) {
    return bookingInfo.value.button_text
  } else {
    return t('et.submit.label');
  }
});

const data = reactive({
  locationfinder: '',
  action: true,
  bia: false,
  salutation: '',
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  day: '',
  time: '',
  data: false,
  privacy: false,
  newsletter: false
});

const selectedStudio = computed(() => {
  if(data.locationfinder && Object.keys(getStudioByName(props.apiJson, data.locationfinder)).length > 0) {
    return getStudioByName(props.apiJson, data.locationfinder);
  }
});

onBeforeMount(() => {
  countryCodes.forEach((cc) => {
    if (cc.country === getCountryFromUrl()) {
      data.phone = '+' + cc.code;
    }
  });
});

const form = useTemplateRef('form');
onMounted(() => {
  new Form(form.value);
});

/**
 * Date/Time
 */
const selectedStudioDates = shallowRef([{},{},{}]);
const selectedStudioDateTimes = ref([]);

watch(() => data.locationfinder, (newValue) => {
  let studio = getStudioByName(props.apiJson, newValue);

  if(Object.keys(studio).length > 0) {
    trackData({
      'event': 'switch_studio'
    });

    selectedStudioDates.value = studio.cbc;
  } else {
    selectedStudioDates.value = [{},{},{}];
  }
});

watch(() => data.firstname, (newValue) => {
  if(newValue.length && isValidLastName.value) {
    trackData({
      'event': 'firstname_lastname_filled'
    });
  }
});
watch(() => data.lastname, (newValue) => {
  if(isValidFirstName.value && newValue.length) {
    trackData({
      'event': 'firstname_lastname_filled'
    });
  }
});

watch(() => data.day, (newValue) => {
  let studio = getStudioByName(props.apiJson, data.locationfinder),
      formattedOptions = [];

  data.time = '';

  if(newValue && (Object.keys(studio).length > 0) && Object.keys(selectedStudioDates.value[0]).length > 0) {

    studio?.cbc.forEach((date) => {
      if(date.day === newValue) {
        formattedOptions = [];

        // format array to match select field requirements
        date.ranges.forEach((range) => {
          formattedOptions.push({
            text: range,
            value: range
          })
        });

        selectedStudioDateTimes.value = formattedOptions;
      }
    });
  } else {
    selectedStudioDateTimes.value = [];
  }
});

/**
 * Form Validation
 */
const isValidStudio = ref(data.locationfinder.length);
const isValidInterest = ref(data.action || data.bia);
const isValidSalutation = ref(false);
const isValidFirstName = ref(false);
const isValidLastName = ref(false);
const isValidEmail = ref(false);
const isValidPhone = ref(false);
const isValidDate = ref(false);
const isValidTime = ref(false);
const isValidData = ref(false);
const isValidPrivacy = ref(false);

const isFormSend = ref(false);
const formSendError = ref(false);
const isSubmitLoading = ref(false);

const forceValidationOnSubmit = ref(false);

const studioRules = [
    (value) => value.length > 0 || t('et.studio.error.empty'),
    (value) => Object.keys(getStudioByName(props.apiJson, value)).length > 0 || t('et.studio.error.studio'),
];
const salutationRules = [(value) => value.length > 0 || t('et.salutation.error.empty') ];
const firstnameRules = [(value) => value.length > 0 || t('et.firstname.error.empty') ];
const lastnameRules = [(value) => value.length > 0 || t('et.lastname.error.empty') ];
const emailRules = [
  (value) => value.length > 0 || t('et.email.error.empty'),
  (value) => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value) || t('et.email.error.email'),
];
const phoneRules = [
    (value) => countryCodes.some((item) => { return value.includes(item.code, 1); }) || t('et.phone.error.cc'),
    (value) => countryCodes.some((item) => { return value.includes(item.code, 1) && (value.length - 1) > item.code.length; }) || t('et.phone.error.empty'),
    (value) => /^\+[0-9\s]*$/.test(value) || t('et.phone.error.digits'),
];
const dateRules = [
    (value) => Object.keys(selectedStudioDates.value[0]).length > 0 || t('et.date.error.studio'),
    (value) => value.length > 0 || t('et.date.error.empty')
];
const timeRules = [
    (value) => data.day.length > 0 || t('et.time.error.date'),
    (value) => value.length > 0 || t('et.time.error.empty'),
];
const dataRules = [(value) => value === true || t('et.data.error.empty') ];
const privacyRules = [(value) => value === true || t('et.privacy.error.empty') ];

const handleStudioValidation = (isValid) => {isValidStudio.value = isValid;};
const handleSalutationValidation = (isValid) => {isValidSalutation.value = isValid;};
const handleFirstNameValidation = (isValid) => {isValidFirstName.value = isValid;};
const handleLastNameValidation = (isValid) => {isValidLastName.value = isValid;};
const handleEmailValidation = (isValid) => {isValidEmail.value = isValid;};
const handlePhoneValidation = (isValid) => {isValidPhone.value = isValid;};
const handleDateValidation = (isValid) => {isValidDate.value = isValid;};
const handleTimeValidation = (isValid) => {isValidTime.value = isValid;};
const handleDataValidation = (isValid) => {isValidData.value = isValid;};
const handlePrivacyValidation = (isValid) => {isValidPrivacy.value = isValid;};

const isFormValid = computed(() => isValidStudio.value && isValidSalutation.value && isValidFirstName.value && isValidLastName.value && isValidEmail.value && isValidPhone.value);

const handleSubmit = () => {
  forceValidationOnSubmit.value = true;
  isSubmitLoading.value = true;

  setTimeout(() => {
    if (isFormValid.value) {
      let formData = {},
          emailSha256 = '',
          phoneSha256 = '';

      formData['salutation'] = data.salutation;
      formData['firstname'] = data.firstname;
      formData['lastname'] = data.lastname;
      formData['email'] = data.email;
      formData['phone'] = data.phone.replace('+', '00');

      emailSha256 = normalizeEmailForUserHash(formData['email']);
      emailSha256 = hashSha256(emailSha256);
      phoneSha256 = hashSha256(formData['phone'])

      formData['action'] = true;
      formData['bia'] = false;

      formData['studio_uid'] = getStudioUidByName(props.apiJson, data.locationfinder);
      formData['booking_uid'] = bookingInfo.value ? bookingInfo.value.uid : '';

      formData['browser'] = getBrowser();
      formData['href'] = window.location.href;

      formData['kies_ad_ref'] = JSON.stringify(decodeURIComponent(getCookie('kies_ad_ref'))) ?? {};
      formData['kies_ad_et'] = JSON.stringify(getKiesAdEtVal()) ?? {};

      axios.post(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/api/v1/conversion/', formData).then(response => {
        console.info(response.status);

        isFormSend.value = true;
        formSendError.value = false;

        trackData({
          'event': 'generate_lead',
          'form_name': 'Probetraining',
          'lead_trial': true,
          'lead_bia': false,
          'studio_id': formData.studio_uid,
          'studio_name': data.locationfinder,
          'booking_id': formData.booking_uid,
          'first_party_data': {
            'sha256_email_address': emailSha256,
            'sha256_phone_number': phoneSha256
          }
        });

        // go to success page on submit
        switch (getLanguageFromUrl()) {
          case 'de':
            window.location.replace(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/probetraining/erfolgreich/?studio=' + formData.studio_uid);
            break;
          case 'en':
            window.location.replace(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/book-now/successful/?studio=' + formData.studio_uid);
            break;
          case 'fr':
            window.location.replace(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/essayez-le-maintenant/reussir/?studio=' + formData.studio_uid);
            break;
        }
      }).catch(error => {
        console.error(error.status);

        // server error
        isFormSend.value = false;
        formSendError.value = true;

        trackData({
          'event': 'generate_lead_error',
          'error': 'server_error',
          'form_name': 'Probetraining'
        });
      });

    } else {
      // form error
      isSubmitLoading.value = false;

      trackData({
        'event': 'generate_lead_error',
        'error': 'form_error',
        'form_name': 'Probetraining'
      });
    }
  }, 0);
};

const getFormattedCallbackDate = () => {
  let time = data.time,
      timestamp = selectedStudioDates.value.filter((date) => date.day === data.day)[0]?.ts,
      date = new Date(timestamp * 1000);

  time = time.split('-');

  return {
    date: data.day + date.getFullYear(),
    time_from: time[0],
    time_to: time[1]
  }
}

const getKiesAdEtVal = () => {
  let adsCookie = initialAdsCookie;

  if(checkCookies()) {
    if(!!getCookie('kies_ad_et')) {
      adsCookie = JSON.parse(decodeURIComponent(getCookie('kies_ad_et')));
    }

    adsCookie.consent = window.uc_kies_ad_et ? 'accepted' : 'declined';
  } else {
    adsCookie.consent = 'browser rejects cookies';
  }

  return adsCookie;
}

</script>